/* ═══════════════════════════════════════════════════════════════
   indent-edit — the ONE write path for level indent / hanging indent.

   Both grab points now drive it: the ruler's Word-style triangles
   (Ruler.tsx) and the in-canvas handles that sit on the selected
   level's own sample paragraph (IndentHandles.tsx). Same units
   (inches), same snapping, same clamps, same undoable forma edit —
   the same contract `shared/zone-space.ts` gives whole-zone spacing.

   Pure except for `applyIndentEdit` / `nudgeIndent`, which are thin
   wrappers over the draft reducer; the math they call is testable on
   its own.
   ═══════════════════════════════════════════════════════════════ */

import type { LevelStyle, StandardConfig } from '../../shared/types';
import type { FormaEdit } from './panels/FormaPalettes';

/** Word's quarter-inch grid. Deliberately defined here rather than imported
 *  from `renderer/utils.ts`: that module assigns to `window` on load, which
 *  would drag the whole renderer global bag into this module and make the
 *  write path untestable outside a browser. Same formula, one line. */
export const snapCoarse = (v: number): number => Math.round(v / 0.25) * 0.25;

/** Which of Word's two markers is being dragged. */
export type IndentKind = 'indent' | 'hanging';

export const PX_PER_IN = 96;
export const pxToIn = (px: number): number => px / PX_PER_IN;
export const inToPxLocal = (inches: number): number => inches * PX_PER_IN;

/** How close (inches) a drag has to get before it snaps to a sibling level. */
export const SNAP_TOLERANCE_IN = 0.08;
/** Keyboard nudge: Word's default tab step, Shift for a 1/16" trim. */
export const NUDGE_COARSE_IN = 0.25;
export const NUDGE_FINE_IN = 0.0625;

const fieldFor = (kind: IndentKind): 'indent' | 'hangingIndent' =>
  kind === 'indent' ? 'indent' : 'hangingIndent';

/**
 * Every OTHER level's indent and wrap edge — the positions a drag snaps
 * to, so levels line up with each other the way a spec expects.
 */
export function indentSnapTargets(
  levels: LevelStyle[] | undefined | null,
  idx: number | null
): number[] {
  if (idx == null) return [];
  const out: number[] = [];
  (levels || []).forEach((l, i) => {
    if (i === idx) return;
    out.push(l.indent || 0);
    out.push((l.indent || 0) + (l.hangingIndent || 0));
  });
  return out;
}

export function findIndentSnap(snaps: number[], v: number): number | null {
  for (const s of snaps) if (Math.abs(s - v) < SNAP_TOLERANCE_IN) return s;
  return null;
}

export interface IndentDragInput {
  kind: IndentKind;
  /** The value the drag started from, in inches. */
  startValue: number;
  /** How far the pointer has travelled, in LAYOUT inches (zoom removed). */
  deltaIn: number;
  /** This level's `indent` — the hanging marker measures its wrap edge from it. */
  baseIndent: number;
  /** Fine mode (held > 1.5s): no snapping, 4-decimal precision. */
  fine: boolean;
  snaps: number[];
}

/**
 * The value a drag lands on. Lifted verbatim from Ruler.tsx's drag
 * handler so the in-canvas handles can never drift from the ruler:
 *
 * - `indent` snaps on its own position.
 * - `hanging` snaps on the WRAP EDGE (indent + hanging) then converts
 *   back, and is clamped at 0 so the wrap edge can never sit left of
 *   the number (Josh, 2026-08-13, Order 3).
 */
export function nextIndentValue(input: IndentDragInput): number {
  const { kind, startValue, deltaIn, baseIndent, fine, snaps } = input;
  if (kind === 'indent') {
    const nv = Math.max(0, startValue + deltaIn);
    return fine ? Math.round(nv * 10000) / 10000 : (findIndentSnap(snaps, nv) ?? snapCoarse(nv));
  }
  let nv = Math.max(0, startValue + deltaIn);
  if (fine) return Math.max(0, Math.round(nv * 10000) / 10000);
  const edge = baseIndent + nv;
  const snapped = findIndentSnap(snaps, edge);
  nv = snapped != null ? snapped - baseIndent : snapCoarse(nv);
  return Math.max(0, nv);
}

/** Write one absolute value onto one level. Never negative. */
export function applyIndentEdit(
  edit: FormaEdit,
  idx: number,
  kind: IndentKind,
  value: number
): void {
  const field = fieldFor(kind);
  const nv = Math.max(0, value);
  edit((f: StandardConfig) => {
    const levels = [...(f.levels || [])];
    if (!levels[idx]) return f;
    levels[idx] = { ...levels[idx], [field]: nv };
    return { ...f, levels };
  });
}

/** Keyboard nudge — reads the current value inside the reducer so it can
 *  never race a live drag's own writes. */
export function nudgeIndent(edit: FormaEdit, idx: number, kind: IndentKind, deltaIn: number): void {
  const field = fieldFor(kind);
  edit((f: StandardConfig) => {
    const levels = [...(f.levels || [])];
    if (!levels[idx]) return f;
    const cur = (levels[idx][field] as number) || 0;
    levels[idx] = { ...levels[idx], [field]: Math.max(0, cur + deltaIn) };
    return { ...f, levels };
  });
}

/** The step an arrow key moves — Shift trims to 1/16". */
export const nudgeStep = (shift: boolean): number => (shift ? NUDGE_FINE_IN : NUDGE_COARSE_IN);
